import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { parseDynamicDate } from '../../../utils/dateUtils';
import { REGEX_PATTERNS } from '@/constants/regex';
import { parsePhoneForInput } from '@/utils/phoneHelpers';
import { getTimezoneFromPhoneCode } from '@/utils/timezoneHelpers';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

/**
 * Checks if a valid LinkedIn URL exists in the form values
 * @param formValues - The form values object containing socials_kind_X and socials_url_X fields
 * @returns boolean - True if a valid LinkedIn URL is found
 */
export const hasValidLinkedInUrl = (formValues) => {
  if (!formValues) return false;

  return Object.keys(formValues).some((key) => {
    if (key.startsWith('socials_kind_')) {
      const id = key.split('_').pop();
      let kindValue = formValues[key];

      // Handle both string and object values (some selects return objects)
      if (typeof kindValue === 'object' && kindValue !== null) {
        kindValue = kindValue.value || kindValue.label || '';
      }

      const urlValue = formValues[`socials_url_${id}`];

      return (
        kindValue?.toLowerCase() === 'linkedin' &&
        urlValue?.trim() &&
        REGEX_PATTERNS.linkedin.test(urlValue)
      );
    }
    return false;
  });
};

// The referral toggle is only meaningful when Source = "Employee Referral".
// Mirrors MarkAsReferredField's gate so the submitted payload can never
// disagree with what the toggle showed. Represents referral *intent* only —
// the create flow additionally requires a Job before submitting (enforced in
// CreateContact's submit handler, which prompts when none is selected).
export const EMPLOYEE_REFERRAL_SOURCE_NAME = 'employee referral';

interface CandidateSource {
  id?: string | number;
  name?: string;
}

export const shouldSubmitAsReferral = ({
  markAsReferred,
  candidateSources,
  sourceId,
}: {
  markAsReferred?: boolean;
  candidateSources?: CandidateSource[];
  sourceId?: string | number;
}): boolean => {
  if (!markAsReferred) return false;

  const selectedSource = candidateSources?.find(
    (source) => source?.id === sourceId
  );

  return (
    selectedSource?.name?.trim().toLowerCase() === EMPLOYEE_REFERRAL_SOURCE_NAME
  );
};

// Create-flow guard: a referral must be attached to a job. Returns true when the
// user intends a referral (see `shouldSubmitAsReferral`) but hasn't selected a
// job — the submit handler shows an error toast and aborts in that case.
export const isReferralMissingJob = ({
  isReferral,
  hasJob,
}: {
  isReferral?: boolean;
  hasJob?: boolean;
}): boolean => Boolean(isReferral) && !hasJob;

export const checkContactMatching = async (
  firstName = '',
  lastName = '',
  email
) => {
  if (!email) return null;

  const firstNameParam = firstName?.toLowerCase() || '';
  const lastNameParam = lastName?.toLowerCase() || '';
  const path = `contacts/matching?first_name=${firstNameParam}&last_name=${lastNameParam}&email=${encodeURIComponent(
    email
  )}`;

  try {
    const response = await fetchSelectOptions({
      method: 'get',
      endPoint: path,
    });
    return response?.contact || null;
  } catch (error) {
    console.error('Error fetching matching contact:', error);
    return null;
  }
};

/**
 * Like checkContactMatching but always fires the API call even without an email.
 * Used in the resume parsing flow to always check for duplicates after generation.
 */
export const fetchMatchingContactFromParsed = async (
  firstName = '',
  lastName = '',
  email = '',
  { linkedin, github, currentContact } = {}
) => {
  let qEmail = email;
  let qLinkedin = linkedin;
  let qGithub = github;

  if (currentContact) {
    const ce =
      currentContact?.emails?.[0]?.email || currentContact?.email || null;
    const cl = currentContact?.social?.linkedin_url;
    const cg = currentContact?.social?.github_url;

    qEmail = email && (!ce || email !== ce) ? email : '';
    qLinkedin = linkedin && (!cl || linkedin !== cl) ? linkedin : '';
    qGithub = github && (!cg || github !== cg) ? github : '';
  }

  if (!qEmail && !qLinkedin && !qGithub) return null;

  const params = new URLSearchParams({
    first_name: firstName?.toLowerCase() || '',
    last_name: lastName?.toLowerCase() || '',
    ...(qEmail ? { email: qEmail } : {}),
    ...(qLinkedin ? { linkedin: qLinkedin } : {}),
    ...(qGithub ? { github: qGithub } : {}),
  });

  try {
    const response = await fetchSelectOptions({
      method: 'get',
      endPoint: `contacts/matching?${params.toString()}`,
    });
    return response?.contact || null;
  } catch (error) {
    console.error('Error fetching matching contact from parsed resume:', error);
    return null;
  }
};

const abbrCountryMap = {
  usa: {
    full_address: 'United States',
    places_country_id: 233,
    places_city_id: null,
    places_state_id: null,
  },
  uk: {
    full_address: 'United Kingdom',
    places_country_id: 232,
    places_city_id: null,
    places_state_id: null,
  },
  uae: {
    full_address: 'United Arab Emirates',
    places_country_id: 231,
    places_city_id: null,
    places_state_id: null,
  },
  nz: {
    full_address: 'New Zealand',
    places_country_id: 158,
    places_city_id: null,
    places_state_id: null,
  },
};

export const getContactInitialValuesFromResume = async (resume, getDegree) => {
  const {
    Name = '',
    MobileNumber,
    PhoneCode,
    LinkedInURL,
    GithubURL = [],
    Emails = [],
    Location,
    Country,
    SkillTags = [],
    Education = [],
    Experience = [],
  } = resume ?? {};
  const [first_name, last_name] =
    typeof Name === 'string' ? (Name?.split(' ') ?? []) : [];
  // ! Country auto selection for abbreviation is handled as quick fix, need to be removed, once BE fix is done
  const supportedAbbreviationCountries = ['usa', 'uk', 'uae', 'nz'];
  let updatedLocation = Location;

  const countryLowerCase = Country?.toLowerCase();
  if (supportedAbbreviationCountries.includes(countryLowerCase)) {
    const fullForm = abbrCountryMap[countryLowerCase].full_address;
    updatedLocation = Location?.split(',')
      .map((part) =>
        part.trim().toLowerCase() === countryLowerCase ? fullForm : part
      )
      .join(', ');
  }

  const fetchLocationOptions = async (updatedLocation) => {
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error('Request timed out for location')),
          3000
        );
      });
      const fetchPromise = fetchSelectOptions({
        endPoint: `/ats_offices/search_location?q=${updatedLocation}`,
        processResponse: (res) => res?.data,
      });

      const result = await Promise.race([fetchPromise, timeoutPromise]);
      return result || [];
    } catch (error) {
      console.error('Fetch error or timeout for location:', error);
      return [];
    }
  };
  const locationVal = updatedLocation
    ? await fetchLocationOptions(updatedLocation)
    : null;

  // Parse phone number and code into the phone input format
  const parsedPhone = parsePhoneForInput(MobileNumber, PhoneCode);

  // Auto-fill timezone based on phone code (only for countries with single timezone)
  const autoTimezone = PhoneCode
    ? getTimezoneFromPhoneCode(PhoneCode)
    : undefined;

  const val = {
    first_name,
    last_name,
    email: Emails?.[0],
    phone_number: parsedPhone,
    location: locationVal?.[0] ?? null,
    skills: SkillTags,
    time_zone: autoTimezone,
  };

  if (Education?.length > 0) {
    // First, sort education by start date (most recent first)
    const sortedEducation = [...Education].sort((a, b) => {
      const yearA = parseInt(a.From) || 0;
      const yearB = parseInt(b.From) || 0;
      return yearB - yearA;
    });

    sortedEducation.forEach((edu, ind) => {
      const id = ind === 0 ? 1 : ind * 8 + ind + 1;
      const degreeId = `education_degree_${id}`;
      const institution = `education_institution_${id}`;
      const startDate = `education_startDate_${id}`;
      const endDate = `education_endDate_${id}`;
      const endDateDisabled = `education_endDateDisabled_${id}`;
      const isPresent = `education_isPresent_${id}`;

      const parsedStartDate = parseDynamicDate(edu?.From);
      const parsedEndDate = parseDynamicDate(edu?.To);

      // Only consider it present if it's the most recent education (ind === 0)
      // and has an empty "To" date
      const isCurrentEducation = ind === 0 && (!edu?.To || edu?.To === '');

      // Set start date
      const sDate = parsedStartDate ? parsedStartDate.toISOString() : null;

      // Set end date based on conditions
      let eDate = null;
      if (!isCurrentEducation) {
        if (!edu?.To || edu?.To === '') {
          // If not current but "To" is empty, use December 31st of the start year
          eDate = parsedStartDate
            ? dayjs(parsedStartDate).endOf('year').toISOString()
            : null;
        } else {
          // Year-only end dates (e.g. "2009") snap to Dec 31 so start=Jan and end=Dec
          const isYearOnly = /^\d{4}$/.test(edu?.To);
          eDate = parsedEndDate
            ? isYearOnly
              ? dayjs(parsedEndDate).endOf('year').toISOString()
              : parsedEndDate.toISOString()
            : null;
        }
      }

      const degree =
        getDegree?.find((degree) => degree.name === edu?.Qualification)?.id ||
        getDegree?.find((degree) => degree.name === 'Other')?.id;
      val[degreeId] = degree;
      val[institution] = edu?.School;
      val[endDateDisabled] = 'Present';
      val[isPresent] = isCurrentEducation;
      val[startDate] = sDate;
      val[endDate] = eDate;
    });
  }

  if (Experience?.length > 0) {
    // Sort experience by start date (most recent first)
    const sortedExperience = [...Experience].sort((a, b) => {
      const dateA = parseDynamicDate(a.From) || new Date(0);
      const dateB = parseDynamicDate(b.From) || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });

    sortedExperience.forEach((exp, ind) => {
      const id = ind === 0 ? 1 : ind * 8 + ind + 1;
      const title = `experience_title_${id}`;
      const company = `experience_company_${id}`;
      const startDate = `experience_startDate_${id}`;
      const endDate = `experience_endDate_${id}`;
      const isPresent = `experience_isPresent_${id}`;
      const endDateDisabled = `experience_endDateDisabled_${id}`;

      const parsedStartDate = parseDynamicDate(exp?.From);
      const parsedEndDate = parseDynamicDate(exp?.To);

      // For experience, we use IsCurrentJob flag or check if it's the most recent
      const isCurrentJob =
        exp?.IsCurrentJob === 1 || (ind === 0 && (!exp?.To || exp?.To === ''));

      // Set dates
      const sDate = parsedStartDate ? parsedStartDate.toISOString() : null;
      const isToYearOnly = /^\d{4}$/.test(exp?.To);
      const eDate = isCurrentJob
        ? null
        : parsedEndDate
          ? isToYearOnly
            ? dayjs(parsedEndDate).endOf('year').toISOString()
            : parsedEndDate.toISOString()
          : null;

      val[endDateDisabled] = 'Present';

      // note: max of 150 characters is allowed for title,
      // if resume parsing gives more than that, trim to 150
      if (typeof exp?.JobTitle === 'string') {
        val[title] = exp.JobTitle.trim();
        if (val[title].length > 150) {
          val[title] = val[title].substring(0, 150);
        }
      }

      val[company] = exp?.Company;
      val[startDate] = sDate;
      val[endDate] = eDate;
      val[isPresent] = isCurrentJob;
    });
  }

  // Only add LinkedIn if it's a valid profile URL (must have something after /in/)
  if (
    LinkedInURL &&
    /^https:\/\/(www\.)?linkedin\.com\/in\/[^/]+\/?$/.test(LinkedInURL.trim())
  ) {
    const formKindId = `socials_kind_1`;
    const formUrlId = `socials_url_1`;

    val[formKindId] = 'linkedin';
    val[formUrlId] = LinkedInURL;
  }

  if (GithubURL?.length > 0) {
    //socials has 4 dynamic fields in creation so ID is 5

    const formKindId = `socials_kind_5`;
    const formUrlId = `socials_url_5`;

    val[formKindId] = 'github';
    val[formUrlId] = GithubURL?.[0];
  }

  return val;
};
